import React, { useEffect } from "react";
import type { StoryMeta } from "@features/devtools/registry/types";
import { JSONTable } from "@shared/json-table";
import type { ColumnDef } from "@shared/json-table";

// Domain model for the demo
export type Person = {
  id: string;
  name: string;
  email: string;
  status: "active" | "paused" | "archived";
  created_at: string;
};

const columns: ColumnDef<Person>[] = [
  { id: "name", header: "Name", accessorKey: "name", enableSort: true, filter: { type: "text", placeholder: "e.g. Alice" } },
  { id: "email", header: "Email", accessorKey: "email", enableSort: true, filter: { type: "text" } },
  { id: "status", header: "Status", accessorKey: "status", enableSort: true, filter: { type: "select", options: [
      { label: "Active", value: "active" },
      { label: "Paused", value: "paused" },
      { label: "Archived", value: "archived" },
  ]}},
  { id: "created_at", header: "Created", accessorKey: "created_at", enableSort: true, filter: { type: "date" } },
];

function FetchMock({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const original = window.fetch;

    function makeRows(offset: number, limit: number): { rows: Person[]; total: number } {
      const total = 137;
      const rows: Person[] = [];
      for (let i = 0; i < limit; i++) {
        const idNum = offset + i + 1;
        if (idNum > total) break;
        rows.push({
          id: String(idNum),
          name: `Person ${idNum}`,
          email: `person${idNum}@example.com`,
          status: idNum % 3 === 0 ? "archived" : idNum % 2 === 0 ? "paused" : "active",
          created_at: new Date(Date.now() - idNum * 86_400_000).toISOString(),
        });
      }
      return { rows, total };
    }

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = typeof input === "string" ? new URL(input, window.location.origin) : new URL((input as Request).url, window.location.origin);
      if (url.pathname.startsWith("/dev-mock/people")) {
        const limit = Number(url.searchParams.get("limit") ?? 20);
        const offset = Number(url.searchParams.get("offset") ?? 0);
        const { rows, total } = makeRows(offset, limit);
        const end = rows.length > 0 ? offset + rows.length - 1 : 0;
        return new Response(JSON.stringify(rows), {
          headers: {
            "Content-Type": "application/json",
            "Content-Range": `${offset}-${end}/${total}`,
          },
          status: 200,
        });
      }
      return original(input as any, init);
    };

    return () => {
      window.fetch = original;
    };
  }, []);

  return <>{children}</>;
}

const meta: StoryMeta<React.ComponentProps<typeof JSONTable<Person>>> = {
  id: "shared/JSONTable",
  title: "JSONTable",
  group: "Shared/Data",
  component: ((props: any) => (
    <FetchMock>
      <JSONTable<Person> {...props} />
    </FetchMock>
  )) as any,
  description: "Generic PostgREST-backed table with URL-synced state.",
  defaultProps: {
    id: "people",
    baseUrl: "/dev-mock",
    resource: "people",
    select: "id,name,email,status,created_at",
    columns,
    globalSearch: { columns: ["name", "email"], placeholder: "Search name or email…" },
    pageSizeOptions: [10, 20, 50],
    initialState: { pageSize: 20, columnVisibility: { email: true, status: true, created_at: true } },
    debug: false,
  } as any,
  controls: {
    debug: { type: "boolean", label: "Debug" },
    pageSizeOptions: { type: "json", label: "Page size options" },
  } as any,
  variants: [
    { name: "Default", props: {} },
    { name: "Big pages", props: { pageSizeOptions: [20, 50, 100] } as any },
  ],
};

export default meta;
